// PublicStart — entry point for a publicly-shared pack URL (/p/:slug).
// Mints a fresh invitation server-side and redirects the browser to
// /i/<token>. Persists the token in localStorage so a refresh or later
// visit from the same browser lands on the same form rather than starting
// a new one.

import { useEffect, useState } from 'react'
import { useLocation, useParams } from 'wouter'
import { getStoredLang } from '@/lib/api'
import { t as tr } from '@/lib/i18n'

// Hours until the next UTC midnight — the window boundary used by the
// /api/public/:slug/start rate-limit counter.
function hoursUntilUtcMidnight(): number {
  const now = new Date()
  const tomorrow = new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() + 1,
      0,
      0,
      0,
      0,
    ),
  )
  const diffMs = tomorrow.getTime() - now.getTime()
  return Math.max(1, Math.round(diffMs / 3_600_000))
}

const STORAGE_PREFIX = 'bi:public-token:'

function storedToken(slug: string): string | null {
  if (typeof window === 'undefined') return null
  return window.localStorage.getItem(STORAGE_PREFIX + slug)
}

function storeToken(slug: string, token: string): void {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(STORAGE_PREFIX + slug, token)
}

export function PublicStart() {
  const params = useParams() as { slug: string }
  const slug = params.slug
  const [, setLocation] = useLocation()
  const [error, setError] = useState<string | null>(null)
  const [rateLimitMessage, setRateLimitMessage] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    const existing = storedToken(slug)
    if (existing) {
      setLocation(`/i/${existing}`, { replace: true })
      return
    }

    fetch(`/api/public/${encodeURIComponent(slug)}/start`, { method: 'POST' })
      .then(async (res) => {
        if (cancelled) return
        if (res.status === 429) {
          const lang = getStoredLang()
          const hours = hoursUntilUtcMidnight()
          const base = tr(lang, 'public_rate_limited')
          setRateLimitMessage(`${base} (~${hours}h)`)
          return
        }
        if (res.status === 404) {
          setError('pack_not_found')
          return
        }
        if (res.status === 403) {
          setError('pack_not_public')
          return
        }
        if (!res.ok) {
          setError('load_failed')
          return
        }
        const { token } = (await res.json()) as { token: string }
        storeToken(slug, token)
        setLocation(`/i/${token}`, { replace: true })
      })
      .catch(() => {
        if (!cancelled) setError('load_failed')
      })

    return () => {
      cancelled = true
    }
  }, [slug, setLocation])

  const lang = getStoredLang()

  if (rateLimitMessage) {
    return (
      <main className="min-h-screen bg-cream flex items-center justify-center px-6 py-12">
        <div className="max-w-md text-center space-y-3">
          <p className="font-serif text-2xl">{tr(lang, 'thanks_small')}</p>
          <p className="font-serif text-base text-muted-foreground">{rateLimitMessage}</p>
        </div>
      </main>
    )
  }

  if (error === 'pack_not_found' || error === 'pack_not_public') {
    return (
      <main className="min-h-screen bg-cream flex items-center justify-center px-6 py-12">
        <div className="max-w-md text-center space-y-3">
          <p className="font-serif text-2xl">{tr(lang, 'invalid_link')}</p>
          <p className="font-serif text-sm text-muted-foreground">
            {tr(lang, 'invalid_link_body')}
          </p>
        </div>
      </main>
    )
  }

  if (error) {
    return (
      <main className="min-h-screen bg-cream flex items-center justify-center px-6 py-12">
        <p className="font-serif text-muted-foreground">{tr(lang, 'load_failed_generic')}</p>
      </main>
    )
  }

  return (
    <main className="min-h-screen bg-cream flex items-center justify-center px-6 py-12">
      <p className="font-serif text-muted-foreground">{tr(lang, 'preparing')}</p>
    </main>
  )
}

export default PublicStart
